import { useState, useEffect, useCallback } from 'react';
import type { Trip, CreateTripData, UpdateTripData } from '../types';
import * as tripService from '../services/tripService';

export interface UseTripsReturn {
  trips: Trip[];
  isLoading: boolean;
  error: string | null;
  createTrip: (data: CreateTripData) => Promise<Trip>;
  updateTrip: (tripId: string, data: UpdateTripData) => Promise<Trip>;
  deleteTrip: (tripId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook that manages the list of trips and CRUD operations.
 * Fetches all user trips from the API and provides methods for create, update, delete.
 */
export function useTrips(): UseTripsReturn {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTrips = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await tripService.fetchTrips();
      setTrips(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trips');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrips();
  }, [loadTrips]);

  const handleCreateTrip = useCallback(async (data: CreateTripData): Promise<Trip> => {
    const newTrip = await tripService.createTrip(data);
    setTrips((prev) => [...prev, newTrip]);
    return newTrip;
  }, []);

  const handleUpdateTrip = useCallback(async (tripId: string, data: UpdateTripData): Promise<Trip> => {
    const updated = await tripService.updateTrip(tripId, data);
    setTrips((prev) => prev.map((t) => (t.id === tripId ? updated : t)));
    return updated;
  }, []);

  const handleDeleteTrip = useCallback(async (tripId: string): Promise<void> => {
    await tripService.deleteTrip(tripId);
    setTrips((prev) => prev.filter((t) => t.id !== tripId));
  }, []);

  return {
    trips,
    isLoading,
    error,
    createTrip: handleCreateTrip,
    updateTrip: handleUpdateTrip,
    deleteTrip: handleDeleteTrip,
    refresh: loadTrips,
  };
}
